import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import ConfirmExitModal from '../../../components/ConfirmExitModal/ConfirmExitModal';
import { fetchGroups } from '../../../store/actions/groupsActions';
import { fetchDashboards } from '../../../store/actions/dashsActions';
import { fetchUserById, updateSingleUser, resetPassword } from '../../../store/actions/userActions';
import TitlePage from '../../../components/general/TitlePage/TitlePage';
import Input from '../../../components/general/Input/Input';
import TitleSectionComponent from '../../../components/intern/TitleSectionComponent/TitleSectionComponent';
import CheckBoxContainer from '../../../components/general/CheckBox/CheckBoxContainer';
import RadioGroupComponent from "../../../components/RadioGroup/RadioGroup";
import { USER_ROLES } from "../../../constants/userRoles";
import Button from '../../../components/general/Button/Button';
import TableDashboardForm from '../../../components/intern/TableInform/TableDashboardForm';
import AlertComponent from '../../../components/intern/Alert/Alert';
import './UserSearchList.css';

const EditarUsuarioPage = () => {
    const [showConfirmExit, setShowConfirmExit] = useState(false);

    const dispatch = useDispatch();
    const { id } = useParams();
    const navigate = useNavigate();

    // Inicialize selectedRole com um valor padrão
    const [selectedRole, setSelectedRole] = useState("client");
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [userNameError, setUserNameError] = useState("");
    const [userEmailError, setUserEmailError] = useState("");
    const [dashboardName, setDashboardName] = useState("");
    const [filteredDashboards, setFilteredDashboards] = useState([]);
    const [selectedDashboards, setSelectedDashboards] = useState([]);
    const [alert, setAlert] = useState(null);
    const alertTimeoutId = useRef(null);
    const [initialData, setInitialData] = useState(null);

    const { groups, loadingGroups, errorGroups } = useSelector((state) => state.groups);
    const { dashs, loadingDashs, errorDashs } = useSelector((state) => state.dashs);
    const { user, loadingUser, errorUser, updateLoading, updateError } = useSelector((state) => state.user);

    const showAlert = (type, message) => {
        if (alertTimeoutId.current) {
            clearTimeout(alertTimeoutId.current);
        }
        setAlert({ type, message });

        alertTimeoutId.current = setTimeout(() => {
            setAlert(null);
            alertTimeoutId.current = null;
        }, 5000);
    };

    useEffect(() => {
        dispatch(fetchGroups());
        dispatch(fetchDashboards());
        if (id) {
            dispatch(fetchUserById(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (user && id) {
            const userData = user.user || user;

            setUserName(userData.userName || "");
            setUserEmail(userData.userEmail || "");
            setIsAdmin(userData.isAdmin || false);
            setSelectedRole(userData.selectedRole || "client");


            if (dashs && userData.dashboardsId) {
                const initialSelectedDashboards = dashs.filter(dashboard =>
                    userData.dashboardsId.includes(dashboard.id)
                );
                setSelectedDashboards(initialSelectedDashboards);
            }

            // Carregar grupos selecionados
            if (userData.groupsId) {
                setSelectedGroups(userData.groupsId);
            }

            setInitialData({
                userName: userData.userName || "",
                userEmail: userData.userEmail || "",
                isAdmin: userData.isAdmin || false,
                selectedRole: userData.selectedRole || "client",
                selectedDashboards: [...(userData.dashboardsId || [])].sort(),
                selectedGroups: [...(userData.groupsId || [])].sort()
            });
        }
    }, [user, id, dashs]);


    const handleGroupSelection = (newSelectedGroupIds) => {
        setSelectedGroups(newSelectedGroupIds);
    };

    const handleUserNameChange = (e) => {
        const value = e.target.value;
        setUserName(value);
        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;
        if (value.length < 3) {
            setUserNameError("O nome completo precisa ter no mínimo 3 caracteres.");
        } else if (!nameRegex.test(value)) {
            setUserNameError("O nome completo não pode conter números ou caracteres especiais.");
        } else {
            setUserNameError("");
        }
    };

    const handleUserEmailChange = (e) => {
        const value = e.target.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setUserEmail(value);
        if (value.length > 255) {
            setUserEmailError("E-mail muito longo! O limite é 255 caracteres.");
        } else if (!emailRegex.test(value) && value.length > 0) {
            setUserEmailError("Formato de e-mail inválido! Certifique-se de que tenha o formato correto (ex: usuario@dominio.com).");
        } else {
            setUserEmailError("");
        }
    };

    const handleRoleChange = (roleValue) => {
        setIsAdmin(roleValue === "masterAdmin");
        setSelectedRole(roleValue);
    };

    const handleDashboardNameChange = (e) => {
        const value = e.target.value;
        setDashboardName(value);
        if (value.length > 0) {
            const matches = dashs.filter(
                (dashboard) =>
                    dashboard.dashboardName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredDashboards(matches);
        } else {
            setFilteredDashboards([]);
        }
    };

    const handleAddDashboard = (dashboardToAdd) => {
        if (!selectedDashboards.some((dash) => dash.id === dashboardToAdd.id)) {
            setSelectedDashboards(prevSelectedDashboards => [...prevSelectedDashboards, dashboardToAdd]);
            setDashboardName("");
            setFilteredDashboards([]);
        }
    };

    const handleRemoveDashboard = (dashboardIdToRemove) => {
        setSelectedDashboards(prevSelectedDashboards =>
            prevSelectedDashboards.filter(dash => dash.id !== dashboardIdToRemove)
        );
        showAlert('excluir');
    };

    const handlePasswordReset = () => {
        try {
            dispatch(resetPassword({ email: userEmail }));
            showAlert('resetar');
        }
        catch (error) {

        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (userNameError || userEmailError || !userName || !userEmail) {
            return;
        }

        try {
            await dispatch(
                updateSingleUser({
                    id,
                    userName,
                    userEmail,
                    isAdmin,
                    selectedRole,
                    approved: true,
                    selectedDashboards: selectedDashboards.map(dash => dash.id),
                    selectedGroups
                })
            ).unwrap();

            showAlert('editar');
            navigate("/dashboard");
        } catch (error) {
            console.error("Erro ao salvar Usuário:", error);
            showAlert('erro');
        }
    };

    const handleBackClick = () => {
        const hasChanges =
            userName !== initialData?.userName ||
            userEmail !== initialData?.userEmail ||
            isAdmin !== initialData?.isAdmin ||
            selectedRole !== initialData?.selectedRole ||
            JSON.stringify([...selectedDashboards.map(d => d.id)].sort()) !== JSON.stringify(initialData?.selectedDashboards) ||
            JSON.stringify([...selectedGroups].sort()) !== JSON.stringify(initialData?.selectedGroups);

        if (hasChanges) {
            setShowConfirmExit(true);
        } else {
            navigate("/dashboard");
        }
    };


    if (loadingGroups || loadingDashs || loadingUser) return <p>Carregando...</p>;
    if (errorGroups || errorDashs || errorUser) return <p>Erro: {errorGroups || errorDashs || errorUser}</p>;

    return (
        <section className="container-extern content-with-sidebar">
            <FontAwesomeIcon
                icon={faArrowLeft}
                size="lg"
                style={{ cursor: "pointer", marginTop: "8px" }}
                onClick={handleBackClick}
            />

            <TitlePage
                title="Editar Usuário"
                subtitle="Atualize os dados do usuário e ajuste as permissões conforme necessário."
            />

            <Helmet>
                <title>Edit User</title>
            </Helmet>

            <form onSubmit={handleSubmit}>
                <Input
                    type="text"
                    placeholder="Digite aqui o nome completo do usuário ..."
                    label="Nome Completo:"
                    value={userName}
                    onChange={handleUserNameChange}
                    errorMessage={userNameError}
                />

                <Input
                    type="text"
                    placeholder="Digite aqui o e-mail de acesso do usuário..."
                    label="E-mail do Usuário:"
                    value={userEmail}
                    onChange={handleUserEmailChange}
                    errorMessage={userEmailError}
                />

                <div className='container-resetpassword'>
                    <Button type="button" variant="primary" className="btn-primary" onClick={handlePasswordReset}>
                        Redefinir Senha
                    </Button>
                </div>

                <RadioGroupComponent
                    title="Tipo de Usuário:"
                    name="role"
                    options={USER_ROLES}
                    value={selectedRole}
                    onChange={handleRoleChange}
                />

                <TitleSectionComponent>Permitir acesso aos dashboards existentes:</TitleSectionComponent>
                <Input
                    type="text"
                    placeholder="Digite aqui nome do dashboard cadastrado"
                    label="Nome do Dashboard:"
                    value={dashboardName}
                    onChange={handleDashboardNameChange}
                />

                {filteredDashboards.length > 0 ? (
                    <ul className="user-search-list common-list-style">
                        {filteredDashboards.map((dash) => (
                            <li
                                key={dash.id}
                                onClick={() => handleAddDashboard(dash)}
                                className="user-search-item"
                            >
                                {dash.dashboardName}
                            </li>
                        ))}
                    </ul>
                ) : (
                    dashboardName && <p className="no-results common-list-style">Nenhum dashboard encontrado com esse nome. Tente novamente.</p>
                )}

                <TableDashboardForm
                    dados={selectedDashboards}
                    tipoContato="Dashboard"
                    exibirAcesso={false}
                    exibirEditar={false}
                    onExcluir={dash => handleRemoveDashboard(dash.id)}
                    onEditar={() => { }}
                />

                <TitleSectionComponent>Permitir acesso aos grupos existentes:</TitleSectionComponent>
                <CheckBoxContainer
                    options={groups.map(group => ({ label: group.groupName, value: group.uid }))}
                    onChange={handleGroupSelection}
                    initialValues={selectedGroups}
                />

                <div className="section-btn-page">
                    <Button
                        type="submit"
                        variant="save"
                        className='btn-add'
                        disabled={
                            updateLoading ||
                            !!userNameError ||
                            !!userEmailError ||
                            !userName ||
                            !userEmail ||
                            !initialData ||
                            (
                                userName === initialData.userName &&
                                userEmail === initialData.userEmail &&
                                isAdmin === initialData.isAdmin &&
                                selectedRole === initialData.selectedRole &&
                                JSON.stringify([...selectedDashboards.map(d => d.id)].sort()) === JSON.stringify(initialData.selectedDashboards) &&
                                JSON.stringify([...selectedGroups].sort()) === JSON.stringify(initialData.selectedGroups)
                            )
                        }
                    >
                        {updateLoading ? "Salvando..." : "Salvar atualizações"}
                    </Button>
                </div>
                {alert && <AlertComponent type={alert.type} onClose={() => setAlert(null)} />}
                {updateError && <p className="error-message">Erro ao atualizar usuário: {updateError}</p>}
            </form>
            <ConfirmExitModal
                isOpen={showConfirmExit}
                onConfirm={() => navigate("/gerenciar")} // no Grupo
                onCancel={() => setShowConfirmExit(false)}
            />
        </section>
    );
};

export default EditarUsuarioPage;